import React, { useMemo, useState } from 'react';
import { View, StyleSheet } from 'react-native';
import { createStackNavigator } from '@react-navigation/stack';
import { NavigationContainer, useNavigationContainerRef } from '@react-navigation/native';

import { DesktopHeader } from '@/components/residents/desktop/DesktopHeader';
import { DesktopSidebar, DesktopSidebarItem } from '@/components/residents/desktop/DesktopSidebar';
import { useResponsive } from '@/hooks/common/useResponsive';
import { useAppSelector } from '@/store';

import { PaymentsScreen } from '@/screens/residents/payments/PaymentsScreen';
// import { PaymentWizardScreen } from '@/screens/residents/payments/PaymentWizardScreen';
import TransactionProcessingScreen from '@/screens/residents/payments/TransactionProcessingScreen';
import TransactionReceivedScreen from '@/screens/residents/payments/TransactionReceivedScreen';
import TransactionTimeoutScreen from '@/screens/residents/payments/TransactionTimeoutScreen';
import AutoPayWizardScreen from '@/screens/residents/payments/AutoPayWizardScreen';
import AddOrChangePaymentMethodModal from '@/screens/residents/payments/AddOrChangePaymentMethodModal';
import ManageMyWalletModal from '@/screens/residents/payments/ManageMyWalletModal';
// TODO: REVISIT FEATURES
import { MaintenanceScreen } from '@/screens/residents/maintenance/MaintenanceScreen';
import { DesktopBulletinsScreen } from '@/screens/residents/bulletins/desktop/DesktopBulletinsScreen';
import DesktopAccountScreen from '@/screens/residents/account/desktop/DesktopAccountScreen';
import { LinkedAccountsScreen } from '@/screens/residents/linkedAccounts/LinkedAccountsScreen';
import { DesktopCommunityResourcesView } from '@/screens/residents/resources/desktop/DesktopCommunityResourcesView';
import { DesktopFAQScreen } from '@/screens/residents/faq/DesktopFAQScreen';
import PaymentSummaryModal from '@/screens/residents/payments/PaymentSummaryModal';
import ConfirmPaymentDetailsModal from '@/screens/residents/payments/ConfirmPaymentDetailsModal';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { RESIDENTS_DESKTOP_COLORS } from '@/constants/residents/desktop/color';
import { NavigationGroup } from '@/constants/residents/residentNavigation';
import { GradientBorderFrame } from '@/components/common/admin/mobile/GradientBorderFrame';
import { WifiConnectionScreen } from '@/screens/residents/wifi/WifiConnectionScreen';
import { DesktopEventsScreen } from '@/screens/residents/events/desktop/DesktopEventsScreen';
import { MessagesScreen } from '@/screens/residents/messages/MessagesScreen';
import { CommunityMarketplaceScreen } from '@/screens/residents/marketplace/CommunityMarketplaceScreen';
import { MarketplaceProfileScreen } from '@/screens/residents/marketplace/MarketplaceProfileScreen';
import { MarketplaceItemDetailScreen } from '@/screens/residents/marketplace/MarketplaceItemDetailScreen';
import { SellerProfileScreen } from '@/screens/residents/marketplace/SellerProfileScreen';
import { MyListingItemDetailScreen } from '@/screens/residents/marketplace/MyListingItemDetailScreen';
import { SavedListingsScreen } from '@/screens/residents/marketplace/SavedListingsScreen';
import { SavedItemDetailScreen } from '@/screens/residents/marketplace/SavedItemDetailScreen';
import { MarketplaceInboxScreen } from '@/screens/residents/marketplace/MarketplaceInboxScreen';
import { InboxConversationScreen } from '@/screens/residents/marketplace/InboxConversationScreen';
import { MarketplaceFollowingScreen } from '@/screens/residents/marketplace/MarketplaceFollowingScreen';
import { MarketplaceListing, MyListing, SavedListing } from '@/types/residents/marketplaceListing';
import { HomeScreen } from '@/screens/residents/home/HomeScreen';
import { CommunityBoardScreen } from '@/screens/residents/corkBoard/CommunityBoardScreen';
import { ThreadDetailScreen } from '@/screens/residents/corkBoard/ThreadDetailScreen';
import { CreateThreadScreen } from '@/screens/residents/corkBoard/CreateThreadScreen';
import { EditThreadScreen } from '@/screens/residents/corkBoard/EditThreadScreen';
import { MyThreadsScreen } from '@/screens/residents/corkBoard/MyThreadsScreen';
import { SavedThreadsScreen } from '@/screens/residents/corkBoard/SavedThreadsScreen';

export type DesktopMainParamList = {
  Home: undefined;
  Payments: undefined;
  PaymentWizard: { 
    leaseId: number; 
    residentId?: number; 
    leaseUser?: any;
    amount?: number;
    payFullAmount?: boolean;
    paymentMethodId?: string | number;
    confirm?: boolean;
    managePaymentMethods?: boolean;
  } | undefined;
  PaymentSummaryModal: { leaseId: number; residentId?: number } | undefined;
  ConfirmPaymentDetails: { 
    leaseId: number; 
    amount: number; 
    payFullAmount: boolean; 
    paymentMethod?: { id?: string | number; brand?: string; last4?: string; type?: string } 
  } | undefined;
  TransactionProcessing: { 
    leaseId: number; 
    residentId?: number; 
    amount?: number; 
    sourceId?: number;
    onSuccess?: string;
    onFailure?: string;
    onTimeout?: string;
  } | undefined;
  TransactionReceived: { 
    leaseId: number; 
    residentId?: number; 
    amount?: number; 
    paymentMethod?: any; 
    calculatedFees?: any;
    onBehalfOfName?: string;
  } | undefined;
  TransactionTimeout: { 
    leaseId: number; 
    residentId?: number 
  } | undefined;
  AutoPayWizard: { 
    leaseId: number; 
    residentId?: number;
    fromTransactionReceived?: boolean;
  } | undefined;
  AddOrChangePaymentMethod: { 
    leaseId: number; 
    residentId?: number; 
    returnTo?: string; 
    paymentType?: 'ach' | 'card';
    successPage?: string;
    managePaymentPage?: string;
  } | undefined;
  ManageMyWallet: { 
    leaseId: number; 
    residentId?: number; 
    selectedMethodId?: string | number; 
    returnTo?: string;
    successPage?: string;
    addOrChangePaymentMethodPage?: string;
  } | undefined;
  LinkedAccounts: undefined;
  Marketplace: undefined;
  MarketplaceProfile: {
    action?: 'sold' | 'removed' | 'reposted';
    listingId?: string;
  } | undefined;
  MarketplaceItemDetail: {
    listing: MarketplaceListing;
  };
  SellerProfile: {
    sellerId: string;
  };
  MyListingItemDetail: {
    listing: MyListing;
  };
  SavedListings: {
    unsavedId?: string;
  } | undefined;
  SavedItemDetail: {
    listing: SavedListing;
  };
  MarketplaceInbox: undefined;
  InboxConversation: {
    conversationId: string;
  };
  MarketplaceFollowing: undefined;
  CommunityBoard: undefined;
  ThreadDetail: { threadId: string };
  CreateThread: undefined;
  EditThread: { threadId: string };
  MyThreads: undefined;
  SavedThreads: undefined;
  Resources: undefined;
  Messages: { initialSubject?: string; existingConversationId?: number } | undefined;
  Maintenance: undefined;
  Events: undefined;
  Wifi: undefined;
  Bulletins: undefined;
  Notifications: undefined;
  Account: { section?: 'notificationSettings' | 'accountInformation' | 'paymentMethods' | 'autopay' | 'resetPassword' } | undefined;
  FAQ: { url?: string } | undefined;
};

const ContentStack = createStackNavigator<DesktopMainParamList>();

export const DesktopNavigator: React.FC = () => {
  const { layoutMode } = useResponsive();
  const { user } = useAppSelector((state) => state.auth);
  const contentNavRef = useNavigationContainerRef<DesktopMainParamList>();
  
  // Hide Payments for Non-Leasing Occupants
  const shouldShowPayments = user?.customerType !== 'non-leasing occupant';
  // Home is always the initial route for desktop
  const initialRoute = 'Home';
  
  const [activeKey, setActiveKey] = useState<keyof DesktopMainParamList>('Home');

  const items: DesktopSidebarItem[] = useMemo(() => {
    const iconColor = (key: keyof DesktopMainParamList) => (activeKey === key ? RESIDENTS_DESKTOP_COLORS.ICON_ACTIVE : RESIDENTS_DESKTOP_COLORS.ICON_INACTIVE);
    
    // Helper to create sidebar item
    const createItem = (
      key: keyof DesktopMainParamList,
      title: string,
      iconKey: keyof typeof RESIDENTS_SVG_COMPONENTS,
      group: NavigationGroup
    ): DesktopSidebarItem => {
      const IconComponent = RESIDENTS_SVG_COMPONENTS[iconKey] as React.FC<{ width?: number; height?: number; color?: string }>;
      return {
        key,
        title,
        icon: <IconComponent width={24} height={24} color={iconColor(key)} />,
        onPress: () => { setActiveKey(key); contentNavRef.navigate(key as any); },
        isActive: activeKey === key,
        group,
      };
    };

    const allItems: (DesktopSidebarItem | false)[] = [
      // QUICK ACTIONS
      createItem('Home', 'Home', 'HOUSE', 'QUICK_ACTIONS'),
      shouldShowPayments && createItem('Payments', 'Payments', 'CREDIT_CARD', 'QUICK_ACTIONS'),
      createItem('Maintenance', 'Maintenance', 'WRENCH', 'QUICK_ACTIONS'),
      createItem('Messages', 'Property Chat', 'CHAT_ICON', 'QUICK_ACTIONS'),
      // COMMUNITY
      createItem('Events', 'Events', 'STAR', 'COMMUNITY'),
      createItem('Marketplace', 'Marketplace', 'SUPERMARKET', 'COMMUNITY'),
      createItem('CommunityBoard', 'Newsfeed', 'CHAT_ICON', 'COMMUNITY'),
      createItem('Notifications', 'Notifications', 'NOTIFICATION', 'COMMUNITY'),
      // MY ACCOUNT
      createItem('Account', 'Settings', 'USER', 'MY_ACCOUNT'),
      createItem('Resources', 'Resources', 'WIDGETS', 'MY_ACCOUNT'),
      createItem('LinkedAccounts', 'Linked Accounts', 'LINKED_ACCOUNTS', 'MY_ACCOUNT'),
      createItem('FAQ', 'FAQ', 'FAQ', 'MY_ACCOUNT'),
    ];

    return allItems.filter(Boolean) as DesktopSidebarItem[];
  }, [activeKey, shouldShowPayments]);

  const navigateToNotifications = () => contentNavRef.navigate('Notifications');
  const navigateToNotificationSettings = () => contentNavRef.navigate('Account', { section: 'notificationSettings' });
  const navigateToMessages = () => contentNavRef.navigate('Messages');

  // Get the title for the header based on active key
  const getHeaderTitle = () => {
    const item = items.find(i => i.key === activeKey);
    return item?.title || 'Resio';
  };

  return (
    <View style={styles.root}>
      <DesktopHeader 
        title={getHeaderTitle()}
        onNavigateToBulletins={navigateToNotifications}
        onNavigateToNotificationSettings={navigateToNotificationSettings}
        onNavigateToChat={navigateToMessages}
      />
      <View style={styles.body}>
        {layoutMode === 'desktop' && (
          <DesktopSidebar items={items} />
        )}
        <View style={styles.contentOuter}> 
          <GradientBorderFrame borderWidth={5} borderRadius={28}>
            <View style={styles.innerFrameSpacing}>
              <GradientBorderFrame borderWidth={2} borderRadius={24} innerStyle={styles.contentInner}>
                <NavigationContainer independent ref={contentNavRef}>
                  <ContentStack.Navigator initialRouteName={initialRoute} screenOptions={{ headerShown: false }}>
                    <ContentStack.Screen name="Home" component={HomeScreen} listeners={{ focus: () => setActiveKey('Home') }} />
                    {shouldShowPayments && (
                      <ContentStack.Screen name="Payments" component={PaymentsScreen} listeners={{ focus: () => setActiveKey('Payments') }} />
                    )}
                    <ContentStack.Screen 
                      name="PaymentSummaryModal" 
                      component={PaymentSummaryModal as unknown as React.ComponentType<any>}
                      options={{
                        headerShown: false,
                        presentation: 'transparentModal',
                        cardStyle: { backgroundColor: 'transparent' },
                        animationEnabled: false,
                      }}
                    />
                    <ContentStack.Screen 
                      name="ConfirmPaymentDetails" 
                      component={ConfirmPaymentDetailsModal as unknown as React.ComponentType<any>}
                      options={{
                        headerShown: false,
                        presentation: 'transparentModal',
                        cardStyle: { backgroundColor: 'transparent' },
                        animationEnabled: false,
                      }}
                    />
                    {/* <ContentStack.Screen name="PaymentWizard" component={PaymentWizardScreen as unknown as React.ComponentType<any>} /> */}
                    <ContentStack.Screen 
                      name="TransactionProcessing" 
                      component={TransactionProcessingScreen as unknown as React.ComponentType<any>} 
                      options={{
                        headerShown: false,
                        presentation: 'transparentModal',
                        cardStyle: { backgroundColor: 'transparent' },
                        animationEnabled: false,
                      }}
                    />
                    <ContentStack.Screen 
                      name="TransactionReceived" 
                      component={TransactionReceivedScreen as unknown as React.ComponentType<any>} 
                      options={{
                        headerShown: false,
                        presentation: 'transparentModal',
                        cardStyle: { backgroundColor: 'transparent' },
                        animationEnabled: false,
                      }}
                    />
                    <ContentStack.Screen 
                      name="TransactionTimeout" 
                      component={TransactionTimeoutScreen as unknown as React.ComponentType<any>} 
                      options={{
                        headerShown: false,
                        presentation: 'transparentModal',
                        cardStyle: { backgroundColor: 'transparent' },
                        animationEnabled: false,
                      }}
                    />
                    <ContentStack.Screen 
                      name="AutoPayWizard" 
                      component={AutoPayWizardScreen as unknown as React.ComponentType<any>} 
                      options={{
                        headerShown: false,
                        presentation: 'transparentModal',
                        cardStyle: { backgroundColor: 'transparent' },
                        animationEnabled: false,
                      }}
                    />
                    <ContentStack.Screen 
                      name="AddOrChangePaymentMethod" 
                      component={AddOrChangePaymentMethodModal as unknown as React.ComponentType<any>}
                      options={{
                        headerShown: false,
                        presentation: 'transparentModal',
                        cardStyle: { backgroundColor: 'transparent' },
                        animationEnabled: false,
                      }}
                    />
                    <ContentStack.Screen 
                      name="ManageMyWallet" 
                      component={ManageMyWalletModal as unknown as React.ComponentType<any>}
                      options={{
                        headerShown: false,
                        presentation: 'transparentModal',
                        cardStyle: { backgroundColor: 'transparent' },
                        animationEnabled: false,
                      }}
                    />
                    <ContentStack.Screen name="LinkedAccounts" component={LinkedAccountsScreen} listeners={{ focus: () => setActiveKey('LinkedAccounts') }} />
                    <ContentStack.Screen name="Marketplace" component={CommunityMarketplaceScreen} listeners={{ focus: () => setActiveKey('Marketplace') }} />
                    <ContentStack.Screen name="MarketplaceProfile" component={MarketplaceProfileScreen} />
                    <ContentStack.Screen name="MarketplaceItemDetail" component={MarketplaceItemDetailScreen} />
                    <ContentStack.Screen name="SellerProfile" component={SellerProfileScreen} />
                    <ContentStack.Screen name="MyListingItemDetail" component={MyListingItemDetailScreen} />
                    <ContentStack.Screen name="SavedListings" component={SavedListingsScreen} />
                    <ContentStack.Screen name="SavedItemDetail" component={SavedItemDetailScreen} />
                    <ContentStack.Screen name="MarketplaceInbox" component={MarketplaceInboxScreen} />
                    <ContentStack.Screen name="InboxConversation" component={InboxConversationScreen} />
                    <ContentStack.Screen name="MarketplaceFollowing" component={MarketplaceFollowingScreen} />
                    <ContentStack.Screen name="CommunityBoard" component={CommunityBoardScreen} listeners={{ focus: () => setActiveKey('CommunityBoard') }} />
                    <ContentStack.Screen name="ThreadDetail" component={ThreadDetailScreen} />
                    <ContentStack.Screen
                      name="CreateThread"
                      component={CreateThreadScreen}
                      options={{
                        headerShown: false,
                        presentation: 'transparentModal',
                        cardStyle: { backgroundColor: 'transparent' },
                        animationEnabled: false,
                      }}
                    />
                    <ContentStack.Screen
                      name="EditThread"
                      component={EditThreadScreen}
                      options={{
                        headerShown: false,
                        presentation: 'transparentModal',
                        cardStyle: { backgroundColor: 'transparent' },
                        animationEnabled: false,
                      }}
                    />
                    <ContentStack.Screen name="MyThreads" component={MyThreadsScreen} />
                    <ContentStack.Screen name="SavedThreads" component={SavedThreadsScreen} />
                    <ContentStack.Screen name="Resources" component={DesktopCommunityResourcesView} listeners={{ focus: () => setActiveKey('Resources') }} />
                    <ContentStack.Screen name="Messages" component={MessagesScreen} listeners={{ focus: () => setActiveKey('Messages') }} />
                    {/* TODO: REVISIT FEATURES */}
                    <ContentStack.Screen name="Maintenance" component={MaintenanceScreen} listeners={{ focus: () => setActiveKey('Maintenance') }} />
                    <ContentStack.Screen name="Events" component={DesktopEventsScreen as unknown as React.ComponentType<any>} listeners={{ focus: () => setActiveKey('Events') }} />
                    {/* TODO: REVISIT FEATURES */}
                    {/* <ContentStack.Screen name="Wifi" component={require('@/screens/residents/wifi/desktop/DesktopWifiScreen').default} listeners={{ focus: () => setActiveKey('Wifi') }} /> */}
                    <ContentStack.Screen name="Bulletins" component={DesktopBulletinsScreen} listeners={{ focus: () => setActiveKey('Bulletins') }} />
                    <ContentStack.Screen name="Notifications" component={require('@/screens/residents/notifications/desktop/DesktopNotificationsScreen').default} listeners={{ focus: () => setActiveKey('Bulletins') }} />
                    <ContentStack.Screen name="Account" component={DesktopAccountScreen as unknown as React.ComponentType<any>} listeners={{ focus: () => setActiveKey('Account') }} />
                    <ContentStack.Screen name="FAQ" component={DesktopFAQScreen} />
                  </ContentStack.Navigator>
                </NavigationContainer>
              </GradientBorderFrame>
            </View>
          </GradientBorderFrame>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: RESIDENTS_DESKTOP_COLORS.PRIMARY_BACKGROUND,
  },
  body: {
    flex: 1,
    flexDirection: 'row',
    paddingLeft: 24,
    paddingRight: 24,
    width: '100%',
    maxWidth: 1536,
    alignSelf: 'center',
  },
  contentOuter: {
    flex: 1,
    paddingRight: 24,
    paddingLeft: 24,
    paddingTop: 8,
  },
  contentInner: {
    flex: 1,
    backgroundColor: RESIDENTS_DESKTOP_COLORS.SECONDARY_BACKGROUND,
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    overflow: 'hidden',
  },
  innerFrameSpacing: {
    flex: 1,
    padding: 2,
  },
});


